










































































































































































import { find, forEach, get, isEmpty, pickBy, each } from 'lodash-es'
import A from '@/utilities/alpha'
import CharacterCount from '@/components/CharacterCount.vue'
import Selectize from './Selectize.vue'
import AddNewInsightsTitle from './AddNewInsightsTitle.vue'
import Redactor from '@/components/Redactor.vue'
import ClientUploadUpdateList from '@/components/ClientUploads/ClientUploadUpdateList.vue'
import ComparisonList from './ComparisonList.vue'
import ConceptAsset from './ConceptAsset.vue'
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import icoLookup from '@/utilities/ico-lookup'
import * as ah from '@/utilities/asset-helpers'
import * as db from '@shared/types/database'
import * as graph from '@shared/types/graphql'

import { ClassicTestType, NewInsight } from '@/utilities/classic-test-types'
import store from '@/store'
import analytics from '../../analytics'
import { Location } from '@/components/Templates/TemplateManager.vue'

interface FieldTypeValue {
  displayAs: string
  defaultValue: any
}

type FieldTypes = Record<string, FieldTypeValue>

interface Validation {
  noTitle?: boolean
}

interface ValidationError {
  message: string
  ref: string
}

interface SetFieldData {
  index: number
  action: string
  field: keyof NewInsight
  payload: any
}

interface SelectableAsset extends db.Prototype {
  selected?: boolean,
  tested?: boolean
}

@Component({
  components: {
    CharacterCount,
    Selectize,
    ClientUploadUpdateList,
    AddNewInsightsTitle,
    Redactor,
    ComparisonList,
    ConceptAsset
  }
})
export default class AddTestClassic extends Vue {
  @Prop({ required: true }) readonly insightType!: ClassicTestType
  @Prop({ required: true }) readonly location!: Location
  @Prop({ default: null }) readonly initialTest!: db.Test | null
  @Prop({ default: () => [] }) readonly experiments!: any[]

  show: Record<string, boolean> = {}
  newInsight: NewInsight = {}
  validationErrors: ValidationError[] = []
  validatedFields = {}
  validation: Validation = {}
  titleCharCount = 0
  sharedAssets = ah.formatGraphPrototypes(store.state.testCreation.projectPrototypes)

  get fieldTypes (): FieldTypes {
    return {
      title: { displayAs: 'Question', defaultValue: '' },
      concept_assets: { displayAs: 'Concept assets', defaultValue: {} },
      description: { displayAs: 'Description', defaultValue: '' },
      additional_info: { displayAs: 'Additional info', defaultValue: '' },
      directory: { displayAs: 'Tags', defaultValue: 'General' },
      prototypes: { displayAs: 'Uploads', defaultValue: {} },
      segment: { displayAs: 'Audiences', defaultValue: 'General Population' },
      comparison: { displayAs: 'Comparison choices', defaultValue: {} },
      comparison_type: { displayAs: 'Comparison type', defaultValue: 'text' },
      related: { displayAs: 'Experiments', defaultValue: [''] }
    }
  }

  get selectableAssets (): Record<string, SelectableAsset> {
    // Filter out non-selectable assets
    return pickBy(this.sharedAssets, (value, key) => {
      if (!value.url) return false
      if (this.newInsight.concept_assets && Object.keys(this.newInsight.concept_assets).length) {
        return value.url !== this.newInsight.concept_assets[Object.keys(this.newInsight.concept_assets)[0]].url
      }
      if (this.newInsight.comparison && Object.keys(this.newInsight.comparison).length) {
        return Object.keys(this.newInsight.comparison).every(comparisonKey => {
          return this.newInsight.comparison![comparisonKey].url !== value.url
        })
      }
      return true
    })
  }

  get isLocationDefaultDraft () {
    return this.location === Location.DefaultDraftTestCreation
  }

  get titleFieldText () {
    return this.isLocationDefaultDraft
      ? 'What are your questions?'
      : this.insightType.title_text
  }

  get formattedAssets (): graph.Prototype[] {
    return ah.formatDbPrototypes(this.newInsight.prototypes!)
  }

  get insightTypeAvailable () {
    return this.insightType
  }

  @Watch('insightType')
  insightTypeChanged (newVal: any, oldVal: any) {
    if ((!oldVal || !newVal) || newVal.name !== oldVal.name) {
      this.resetForm(this.initialTest)
    }
  }

  resetComparison () {
    this.newInsight.comparison = {}
  }

  mounted () {
    this.resetForm(this.initialTest)
  }

  editUpload (upload: graph.Prototype) {
    this.newInsight.prototypes = ah.editUpload(this.newInsight.prototypes!, upload)
  }

  addUploads (uploads: Record<string, db.Prototype>) {
    this.newInsight.prototypes = ah.addUploads(this.newInsight.prototypes!, uploads)
    analytics.track('Assets Uploaded (Test Creation)', {
      projectId: store.state.testCreation.projectId,
      usedTemplateTest: false
    })
  }

  removeUpload (assetKey: string) {
    this.newInsight.prototypes = ah.removeUpload(this.newInsight.prototypes!, assetKey)
  }

  resetForm (initialTest: db.Test | boolean | null = false) {
    this.show = {}
    const newInsight: NewInsight = {}
    if (!this.insightType) {
      return this.clearState()
    }
    this.insightType.required.forEach(field => {
      let usedField = field
      // Special case for now to handle old/new versions of this modal
      if (usedField === 'title') {
        usedField = 'customerQuestions'
      }
      const fieldVal = initialTest ? get(this, ['initialTest', 'payload', usedField], this.fieldTypes[field].defaultValue) : this.fieldTypes[field].defaultValue
      newInsight[field] = fieldVal
      this.$set(this.show, field, true)
    })
    this.insightType.optional.forEach(field => {
      const fieldVal = initialTest ? get(this, ['initialTest', 'payload', field], this.fieldTypes[field].defaultValue) : this.fieldTypes[field].defaultValue
      // special case of prototypes, divy them out to the appropriate sections
      const conceptAssets: Record<string, db.Prototype> = {}
      const comparisonAssets: Record<string, db.Prototype> = {}
      const uploads: Record<string, db.Prototype> = {}
      if (field === 'prototypes' && initialTest) {
        forEach(fieldVal, (proto, key) => {
          if (this.insightType.name === 'Product Concept Testing' && proto.conceptPrototype) {
            conceptAssets[key] = proto
            this.$set(this.show, 'conceptAssetPicker', true)
          } else if (this.insightType.name === 'Comparison' && proto.comparisonPrototype) {
            comparisonAssets[key] = proto
          } else {
            // Account for plain uploads for tests like User Discovery
            uploads[key] = proto
            this.toggleSharedAsset(key, true)
          }
        })
        // fieldVal = normalAssets
        if (Object.keys(conceptAssets).length) {
          newInsight.concept_assets = conceptAssets
        }
        if (Object.keys(comparisonAssets).length) {
          newInsight.comparison = comparisonAssets
        }
        newInsight.prototypes = uploads
      } else {
        // Normal case, not prototypes field
        newInsight[field] = fieldVal
      }
      // Except for 'related experiments', only show fields by default that have a value
      const showSetting = ['related'].includes(field) ? false : !isEmpty(fieldVal)
      this.$set(this.show, field, showSetting)
    })

    if (this.experiments.length) {
      this.show.related = true
    }

    // Reset Validation Error Checking Values Once Reset
    this.$set(this, 'validationErrors', [])
    this.$set(this, 'validation', {})
    this.$set(this, 'newInsight', newInsight)
  }

  clearState () {
    this.$set(this, 'validationErrors', [])
    this.$set(this, 'validation', {})
    this.$set(this, 'show', {})
  }

  toggleField (field: string, action: string) {
    if (action === 'open') {
      this.$set(this.show, field, true)
    } else if (action === 'close') {
      this.$set(this.show, field, false)
    } else {
      // Toggle
      this.$set(this.show, field, !this.show[field])
    }
  }

  toggleSharedAsset (index: string, value?: boolean) {
    if (this.sharedAssets[index]) {
      this.$set(this.sharedAssets[index], 'selected', !this.sharedAssets[index].selected)
      this.$forceUpdate()
    }
  }

  // update character count when creating a new test
  updateCharacterCount (data: string) {
    this.titleCharCount = A.stripHTML(data).length
  }

  /* Set and Reset */
  setField (field: keyof NewInsight, data: SetFieldData) {
    delete data.field
    const insightField = this.newInsight[field] as unknown as Array<any>
    if (data.action === 'splice') {
      insightField.splice(data.index, 1)
    } else if (data.action === 'modify') {
      insightField[data.index] = data.payload
    } else if (data.action === 'push') {
      insightField.push(data.payload)
    } else {
      this.$set(this.newInsight, field, data)
    }
  }

  resetField (field: string) {
    this.$set(this.newInsight, field, this.fieldTypes[field].defaultValue)
  }

  validateForm (draft = false) {
    // console.log(this.newInsight);
    const validationErrors: ValidationError[] = []
    const validation: Validation = {}
    // Ensure validation error object accounts for client using test templates or not
    this.validateTitle(validation)
    this.validatePrototypesGlobal(validationErrors)
    return { validationErrors, validation }
  }

  validateTitle (validation: Validation) {
    if (A.stripHTML(this.newInsight.title) === '') {
      // Track validation using a flag instead of adding to the validation error array
      validation.noTitle = true
    } else {
      // Ensure if text is entered we update the flag
      validation.noTitle = false
    }
  }

  validatePrototypesGlobal (validationErrors: ValidationError[], options = {}) {
    const prototypeKeys = Object.keys(this.newInsight.prototypes || {})
    prototypeKeys.forEach((key) => {
      const prototype = this.newInsight.prototypes![key]
      if (!prototype.title.trim()) {
        validationErrors.push({
          message: 'All assets require a title',
          ref: key
        })
      }
      // Must have a URL
      if (!prototype.url?.trim()) {
        validationErrors.push({
          message: 'All assets require a URL',
          ref: key
        })
      }
      if (!A.isValidUrl(prototype.url!)) {
        validationErrors.push({
          message: 'Asset URLs must be properly formed',
          ref: key
        })
      }
    })
  }

  getAssetImg (asset: graph.Prototype) {
    var img: string = asset.assetType ? icoLookup[asset.assetType] : '/img/asset_icons/upload.svg'
    var url = asset.url ?? '';
    ['png', 'jpg', 'jpeg', 'gif'].forEach(ext => {
      if (url.slice(ext.length * -1).toLowerCase() === ext) {
        img = url
      }
    })
    return img
  }

  // properly captures data from stage navigation wrapper
  /* Submit */
  clearValidation () {
    this.validationErrors = []
    this.validation = {}
  }

  submit (draft: boolean, validate = true) {
    this.clearValidation()
    if (validate) {
      const { validation, validationErrors } = this.validateForm(draft)
      // All validation points should be ran on test that are submitted, only check the title for test in draft
      const { noTitle } = validation
      let anyErrors

      if (draft) {
        anyErrors = noTitle
      } else {
        const hasAdditionalAssetErrors = validationErrors.length
        anyErrors = hasAdditionalAssetErrors || noTitle
      }

      if (anyErrors) {
        this.validationErrors = validationErrors
        this.validation = validation
        return false
      }
    }

    var newInsight = A.clone(this.newInsight)
    newInsight.title = validate ? A.stripHTML(newInsight.title) : newInsight.title
    newInsight.draft = draft

    // Clean up some of the data before submitting
    if (this.sharedAssets) {
      each(this.sharedAssets, function (sharedAsset, key) {
        if (sharedAsset.selected) {
          newInsight.prototypes![key] = sharedAsset as any
          delete newInsight.prototypes![key].selected
          // Clear these values entirely -- we'll re-add if needed.
          delete newInsight.prototypes![key].conceptPrototype
          delete newInsight.prototypes![key].comparisonPrototype
          delete newInsight.prototypes![key].tested
        }
      })
    }

    // If there are concept assets, add them to the prototypes object and add concept field to them
    if (newInsight.concept_assets) {
      forEach(newInsight.concept_assets, (asset, key) => {
        // Validate image and link comparisons and skip bad ones
        if (asset.asset_type === '' || asset.asset_type === 'url') {
          if (!asset.url || !asset.title || !asset.url.length || !asset.title.length) {
            return
          }
        }

        var preExistingPrototype = find(newInsight.prototypes, (proto, key) => {
          return asset.url === proto.url
        })

        if (preExistingPrototype) {
          preExistingPrototype.conceptPrototype = true
        } else {
          var newAsset = A.clone(asset)
          newAsset.conceptPrototype = true
          newInsight.prototypes![key] = newAsset
        }
      })

      delete newInsight.concept_assets // don't actually want this in the payload
    }

    // If there are comparisons, add them to the prototypes object as appropriate
    if (newInsight.comparison) {
      if (newInsight.comparison_type === 'text') {
        forEach(newInsight.comparison, function (comparison, key) {
          if (comparison.title && comparison.title.length) {
            newInsight.prototypes![key] = Object.assign({}, comparison, { comparisonPrototype: true })
          }
        })
      } else {
        forEach(newInsight.comparison, function (comparison, key) {
          if (comparison.url && comparison.title && comparison.url.length && comparison.title.length) {
            var preExistingPrototype = find(newInsight.prototypes, function (proto, key) {
              return comparison.url === proto.url
            })
            if (preExistingPrototype) {
              preExistingPrototype.comparisonPrototype = true
            } else {
              newInsight.prototypes![key] = Object.assign({}, comparison, { comparisonPrototype: true })
            }
          }
        })
      }
      delete newInsight.comparison // don't actually want this in the payload
    }

    // Dont send fields that are false because the route is still not mature enough to handle them; Todo change that
    Object.keys(newInsight).forEach(key => {
      if ((newInsight as any)[key] === null) {
        delete (newInsight as any)[key]
      }
    })

    if (newInsight.description) {
      newInsight.description = newInsight.description.trim()
    }

    return { newInsight }
  }
}
